import {useRef} from 'react';

import {Alert, SimpleGrid} from '@mantine/core';
import {useNavigate} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

import {useUsageEvents} from '../hooks/useUsageEvents';
import {useUsageEventsSearch} from '../hooks/useUsageEventsSearch';
import {useUsageEventsTableColumns} from '../hooks/useUsageEventsTableColumns';
import {UsageEventRow} from '../types';
import {moduleConfig, MAX_AUTO_DRAIN_PAGES, PAGE_SIZE, usageEventPath} from '../config';

type EmptyState = { color: string, title: string, body: string };

// THREE empty-ish states, not one (§3, §13): no events ingested, a drawer filter that
// matched nothing, and a keyword that matched nothing in the loaded rows. They mean
// different things, and `noRecordsText` is a single string, so this is a conditional
// Alert instead (the defect-signatures form, FNP-923).
const emptyState = (keyword: string, filterCount: number, drainCapped: boolean): EmptyState => {
    if (keyword) {
        return {
            color: 'gray',
            title: I18n.get('No matching usage events'),
            // The footer already states that no loaded row matches; this adds only what it
            // cannot: how to get the rows back, and that unloaded pages were not searched.
            body: I18n.get('Clear the search to see every loaded row. A match may still be on a page that has not been loaded.'),
        };
    }
    if (drainCapped) {
        return {
            color: 'gray',
            title: I18n.get('No match in the newest events yet'),
            body: `${I18n.get('No event matched the filter in the newest')} ${MAX_AUTO_DRAIN_PAGES * PAGE_SIZE} ${I18n.get('scanned. Older events remain: use Load more to keep looking, or clear the filter.')}`,
        };
    }
    if (filterCount > 0) {
        return {
            color: 'gray',
            title: I18n.get('No usage events match the filter'),
            body: I18n.get('Every event was checked and none matches. Clear the filter to see all events.'),
        };
    }
    return {
        color: 'blue',
        title: I18n.get('No usage events yet'),
        body: I18n.get('No metering event has been ingested in this environment. This is not an error.'),
    };
};

export const RecordsTable = () => {
    const {columns} = useUsageEventsTableColumns();
    const navigate = useNavigate();

    const {
        items,
        visibleItems,
        filters,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        nextToken,
        isFetchingMore,
        drainCapped,
        fetchRecords,
    } = useUsageEvents();

    // The same module-scoped store the list hook filters on (FNP-815): this component
    // owns the input, useUsageEvents owns the filtering.
    const {keyword, handleFilterByKeyword} = useUsageEventsSearch();
    const term = keyword.trim();

    const scrollViewportRef = useRef<HTMLDivElement>(null);
    const empty = emptyState(term, Object.keys(filters).length, drainCapped);

    // Read-only (FNP-536): no selection and no batch actions — the ingestion writer owns
    // the lifecycle and there is no operation to apply to a selection. Row click (and the
    // link in the Event cell) opens the detail view; the composite id is encoded by
    // usageEventPath.
    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load usage events')}>
                    {I18n.get('The billing API did not return the usage events. Try Reload; if it persists the API may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<UsageEventRow>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far — the same scope the
                // keyword search works on.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) => navigate(usageEventPath(record.id))}
            />

            {/* Gated on !loading so it does not flash during the first fetch or the drain,
                and on !isError so a failed load keeps its own Alert. */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert color={empty.color} title={empty.title} data-testid='usage-events-empty-state'>
                    {empty.body}
                </Alert>
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={term}
                noun={moduleConfig.noun}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
